import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * FestivalFormModal · alta y edición manual de festivales por admin.
 *
 * Se comparte para ambos flujos (crear/editar) — si recibe `festival`
 * hace PATCH, si no hace POST. `onSaved(nuevo)` propaga el cambio al
 * padre para refrescar la tabla sin recargar toda la lista.
 *
 * Edición mínima viable: nombre, tipo, país, ciudad, web, descripción,
 * arrays multi-select (formats/regions/themes), y UNA SOLA edición
 * (year/deadline/event_start/event_end). Si el festival tenía múltiples
 * ediciones, solo mostramos y editamos la primera para no complicar el
 * form; el resto se conservan intactas. Añadir/editar más ediciones se
 * puede hacer via API directamente (por ahora).
 */

const TYPES = [
  ["festival", "Festival"], ["market", "Mercado"], ["pitch", "Pitch"],
  ["forum", "Foro"], ["residency", "Residencia"], ["lab", "Lab"],
];
const FORMATS = [
  ["documental", "Documental"], ["ficcion", "Ficción"], ["cortometraje", "Corto"],
  ["serie", "Serie"], ["animacion", "Animación"], ["experimental", "Experimental"],
  ["vr_inmersivo", "VR/Inmersivo"],
];
const REGIONS = [
  ["espana", "España"], ["iberoamerica", "Iberoamérica"], ["europa", "Europa"],
  ["norteamerica", "Norteamérica"], ["internacional", "Internacional"],
];

export default function FestivalFormModal({ open, festival, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Reinicializar cuando cambia el festival editado o cuando se abre.
  useEffect(() => {
    if (!open) return;
    const ed = festival?.editions?.[0] || {};
    setForm({
      name: festival?.name || "",
      type: festival?.type || "festival",
      country: festival?.country || "",
      city: festival?.city || "",
      website: festival?.website || "",
      description: festival?.description || "",
      submission_fee_note: festival?.submission_fee_note || "",
      formats: festival?.formats || [],
      regions: festival?.regions || [],
      themes: festival?.themes || [],
      edition_year: ed.year || new Date().getFullYear(),
      edition_submission_deadline: ed.submission_deadline || "",
      edition_event_start: ed.event_start || "",
      edition_event_end: ed.event_end || "",
      edition_is_confirmed: !!ed.is_confirmed,
    });
    setErr("");
  }, [open, festival]);

  if (!open || !form) return null;

  const toggle = (field, value) => setForm(f => ({
    ...f,
    [field]: f[field].includes(value) ? f[field].filter(v => v !== value) : [...f[field], value],
  }));

  const save = async () => {
    setErr("");
    if (!form.name.trim()) { setErr("El nombre es obligatorio."); return; }
    if (!form.country.trim()) { setErr("El país es obligatorio."); return; }
    if (form.formats.length === 0) { setErr("Selecciona al menos un formato."); return; }
    setBusy(true);
    try {
      // Reconstruye editions: si estamos editando, preserva todas las ediciones
      // salvo la primera (que sustituimos por lo del form). Si es nuevo, una sola.
      const yearInt = parseInt(form.edition_year, 10);
      const newEdition = {
        year: Number.isFinite(yearInt) ? yearInt : new Date().getFullYear(),
        submission_deadline: form.edition_submission_deadline || null,
        event_start: form.edition_event_start || null,
        event_end: form.edition_event_end || null,
        is_confirmed: form.edition_is_confirmed,
      };
      const editions = festival?.editions?.length
        ? [newEdition, ...festival.editions.slice(1)]
        : [newEdition];

      const payload = {
        name: form.name.trim(),
        type: form.type,
        country: form.country.trim(),
        city: form.city.trim(),
        website: form.website.trim(),
        description: form.description.trim(),
        submission_fee_note: form.submission_fee_note.trim(),
        formats: form.formats,
        regions: form.regions,
        themes: form.themes,
        editions,
      };
      let saved;
      if (festival) {
        const r = await api.patch(`/festivals/${festival.id}`, payload);
        saved = r.data;
      } else {
        payload.source = "user_added";
        const r = await api.post(`/festivals`, payload);
        saved = r.data;
      }
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al guardar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
         onClick={() => !busy && onClose?.()}
         data-testid="festival-form-modal">
      <div className="relative flex max-h-[92vh] w-[820px] max-w-full flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between border-b border-stone-200 px-5 py-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#941620]">
              {festival ? "Editar festival" : "Añadir festival"}
            </p>
            <p className="mt-0.5 font-serif text-base text-stone-900">
              {festival ? festival.name : "Nuevo festival, mercado o pitch"}
            </p>
          </div>
          <button onClick={onClose} disabled={busy}
                  data-testid="festival-form-close"
                  className="rounded-full border border-stone-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 transition hover:border-[#941620] hover:text-[#941620] disabled:opacity-40">
            ✕
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nombre *">
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                     data-testid="festival-form-name"
                     className="input-gb" />
            </Field>
            <Field label="Tipo *">
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                      data-testid="festival-form-type"
                      className="input-gb">
                {TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="País *">
              <input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })}
                     data-testid="festival-form-country"
                     className="input-gb" />
            </Field>
            <Field label="Ciudad">
              <input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })}
                     data-testid="festival-form-city"
                     className="input-gb" />
            </Field>
            <Field label="Web (URL)" full>
              <input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
                     placeholder="https://…"
                     data-testid="festival-form-website"
                     className="input-gb" />
            </Field>
            <Field label="Descripción" full>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                        rows={3}
                        data-testid="festival-form-description"
                        className="input-gb resize-none" />
            </Field>
            <Field label="Coste de inscripción (nota)" full>
              <input value={form.submission_fee_note} onChange={e => setForm({ ...form, submission_fee_note: e.target.value })}
                     placeholder="p.ej. Gratuito hasta 15 min · 30€ largometraje"
                     data-testid="festival-form-fee"
                     className="input-gb" />
            </Field>
          </div>

          <Section title="Formatos *">
            <ChipRow options={FORMATS} selected={form.formats} onToggle={v => toggle("formats", v)} testidPrefix="fmt" />
          </Section>
          <Section title="Regiones">
            <ChipRow options={REGIONS} selected={form.regions} onToggle={v => toggle("regions", v)} testidPrefix="reg" />
          </Section>
          <Section title="Temáticas (opcional, separadas por coma)">
            <input value={form.themes.join(", ")} onChange={e => setForm({ ...form, themes: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                   placeholder="medioambiental, memoria, LGBTQ+…"
                   data-testid="festival-form-themes"
                   className="input-gb" />
          </Section>

          <Section title="Próxima edición">
            <div className="grid grid-cols-4 gap-3">
              <Field label="Año">
                <input type="number" value={form.edition_year} onChange={e => setForm({ ...form, edition_year: e.target.value })}
                       data-testid="festival-form-edition-year"
                       className="input-gb" />
              </Field>
              <Field label="Deadline inscripción">
                <input type="date" value={form.edition_submission_deadline} onChange={e => setForm({ ...form, edition_submission_deadline: e.target.value })}
                       data-testid="festival-form-deadline"
                       className="input-gb" />
              </Field>
              <Field label="Inicio evento">
                <input type="date" value={form.edition_event_start} onChange={e => setForm({ ...form, edition_event_start: e.target.value })}
                       data-testid="festival-form-event-start"
                       className="input-gb" />
              </Field>
              <Field label="Fin evento">
                <input type="date" value={form.edition_event_end} onChange={e => setForm({ ...form, edition_event_end: e.target.value })}
                       data-testid="festival-form-event-end"
                       className="input-gb" />
              </Field>
            </div>
            <label className="mt-3 flex items-center gap-2 text-[12px] text-stone-700">
              <input type="checkbox" checked={form.edition_is_confirmed}
                     onChange={e => setForm({ ...form, edition_is_confirmed: e.target.checked })}
                     data-testid="festival-form-confirmed"
                     className="h-4 w-4 accent-[#26775E]" />
              Fechas confirmadas oficialmente (marca solo si vienen de la web del festival)
            </label>
          </Section>

          {err && (
            <div className="mt-4 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
              {err}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3">
          <button onClick={onClose} disabled={busy}
                  className="rounded-full border border-stone-400 bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-stone-700 hover:border-stone-600 disabled:opacity-40">
            Cancelar
          </button>
          <button onClick={save} disabled={busy}
                  data-testid="festival-form-save"
                  className="rounded-full bg-[#941620] px-5 py-2 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#7a1119] disabled:opacity-40">
            {busy ? "Guardando…" : (festival ? "Guardar cambios" : "Crear festival")}
          </button>
        </footer>
      </div>
      <style>{`.input-gb { width:100%; border:1px solid #d6d3d1; border-radius:6px; padding:6px 10px; font-size:13px; outline:none; }
      .input-gb:focus { border-color:#941620; }`}</style>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "col-span-2" : ""}`}>
      <span className="text-[10px] font-semibold uppercase tracking-widest text-stone-500">{label}</span>
      {children}
    </label>
  );
}
function Section({ title, children }) {
  return (
    <div className="mt-4">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-stone-500">{title}</p>
      {children}
    </div>
  );
}
function ChipRow({ options, selected, onToggle, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(([k, v]) => (
        <button key={k} type="button" onClick={() => onToggle(k)}
                data-testid={`chip-${testidPrefix}-${k}`}
                className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-widest transition ${
                  selected.includes(k)
                    ? "border-[#941620] bg-[#941620]/10 text-[#941620]"
                    : "border-stone-300 bg-white text-stone-600 hover:border-stone-500"
                }`}>
          {v}
        </button>
      ))}
    </div>
  );
}
